import {
  ArrowRight,
  MessageCircle,
  MessageCircleReply,
  MessagesSquare,
  ShieldCheck,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { IMAGE_URLS } from 'src/assets/urlLinks'
import { buildLoginSignupPath } from 'src/routes/paths'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { Highlighter } from './Highlighter'

const TREYD_LOGO =
  'https://www.datocms-assets.com/67452/1720015305-group-37300.svg'

export const HomeHero = () => {
  const navigate = useNavigate()
  const { t } = useLocalization()

  return (
    <Column className="relative box-border h-[calc(100dvh-3.5rem)] w-full bg-white p-4 md:p-6">
      <Column className="relative mx-auto h-full w-full max-w-6xl overflow-hidden rounded-[32px] border border-stone-300/80 bg-[linear-gradient(165deg,#fbfbfc_0%,#f4f5f7_38%,#eef0f3_72%,#e8eaee_100%)] px-4 py-8 md:rounded-[44px] md:px-8 md:py-10">
        <div
          className="absolute inset-0 [background-image:linear-gradient(to_right,rgba(0,0,0,0.025)_1px,transparent_1px),linear-gradient(to_bottom,rgba(0,0,0,0.025)_1px,transparent_1px)] [background-size:44px_44px] [mask-image:radial-gradient(circle_at_50%_42%,black,transparent_72%)] [-webkit-mask-image:radial-gradient(circle_at_50%_42%,black,transparent_72%)]"
          aria-hidden
        />

        <div
          className="pointer-events-none absolute inset-0 flex items-center justify-center"
          aria-hidden
        >
          <div className="absolute size-[420px] rounded-full border border-stone-200/70 md:size-[560px]" />
          <div className="absolute size-[580px] rounded-full border border-stone-200/50 md:size-[760px]" />
          <div className="absolute size-[740px] rounded-full border border-stone-200/35 md:size-[960px]" />
        </div>

        <div className="min-h-0 flex-1" aria-hidden />

        <Column className="relative z-10 mx-auto w-full min-w-0 shrink-0 items-center text-center">
          <Column className="w-full min-w-0 items-center gap-10 md:gap-12">
            <Column className="w-full max-w-3xl min-w-0 items-center gap-4 text-center">
              <h1 className="font-inter m-0 w-full min-w-0 text-center text-4xl font-normal !leading-[1.12] md:text-5xl lg:text-6xl">
                <span className="bg-gradient-to-b from-black to-neutral-600 bg-clip-text text-transparent">
                  {t('Your new ', 'Er nya ')}
                </span>
                <Highlighter>
                  <span className="text-neutral-800">
                    {t(
                      'full-service merch partner',
                      'fullservice merchpartner'
                    )}
                  </span>
                </Highlighter>
              </h1>

              <p className="font-inter m-0 mx-auto max-w-2xl text-center text-lg !font-normal tracking-tight text-neutral-700 md:text-xl">
                {t(
                  'From the first idea to delivery, work with us in one shared workspace, with product ideas, mockups and the brief in the same place.',
                  'Från första idé till leverans arbetar ni med oss i en gemensam arbetsyta, med produktidéer, mockups och briefen på samma ställe.'
                )}
              </p>
            </Column>

            <Row className="flex-wrap items-center justify-center gap-3">
              <Button
                size="lg"
                radius="full"
                variant="primaryOrange"
                onClick={() => navigate(buildLoginSignupPath())}
              >
                {t('Get started for free', 'Kom igång gratis')}
                <ArrowRight className="ml-2 size-5" strokeWidth={1.75} />
              </Button>
              <div className="relative">
                <Button
                  size="lg"
                  radius="full"
                  variant="secondary"
                  onClick={() => {
                    document
                      .getElementById('try-sara')
                      ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }}
                >
                  <MessagesSquare className="mr-2 size-5" strokeWidth={1.75} />
                  {t('Try our agent', 'Prova vår agent')}
                </Button>
                <span className="pointer-events-none absolute -top-2 right-3 rounded-full bg-violet-600 px-1.5 py-px text-[10px] font-medium leading-4 text-white">
                  {t('New', 'Ny')}
                </span>
              </div>
            </Row>
          </Column>

          <Column className="w-full max-w-3xl items-center gap-4 pt-10">
            <Row className="items-center justify-center gap-2 text-center">
              <ShieldCheck
                className="size-5 shrink-0 text-blue-500 stroke-none [&>path:first-of-type]:fill-current [&>path:first-of-type]:stroke-none [&>path:last-of-type]:fill-none [&>path:last-of-type]:stroke-white [&>path:last-of-type]:stroke-[2]"
                strokeWidth={0}
                aria-hidden
              />
              <p className="m-0 max-w-xl text-[15px] leading-snug text-muted-foreground">
                {t(
                  'Trusted by leading companies since 2015',
                  'Betrodd av ledande företag sedan 2015'
                )}
              </p>
            </Row>

            <Row className="flex-wrap items-center justify-center gap-x-6 gap-y-4 md:gap-x-10">
              <img
                src={IMAGE_URLS.logos.ica}
                alt="ICA"
                className="h-[1.3rem] opacity-50 transition-opacity hover:opacity-80 [filter:brightness(0)]"
              />
              <img
                src={IMAGE_URLS.logos.luminar}
                alt="Luminar"
                className="mb-0.5 h-[1.5rem] opacity-50 transition-opacity hover:opacity-80 [filter:brightness(0)]"
              />
              <img
                src={TREYD_LOGO}
                alt="Treyd"
                className="h-[1.3rem] opacity-50 transition-opacity hover:opacity-80 [filter:brightness(0)]"
              />
              <img
                src={IMAGE_URLS.logos.rift}
                alt="Rift"
                className="h-[1.9rem] opacity-50 transition-opacity hover:opacity-80 [filter:brightness(0)]"
              />
            </Row>
          </Column>
        </Column>

        <div className="min-h-0 flex-1" aria-hidden />
      </Column>
    </Column>
  )
}
